import { endOfDay, endOfHour, startOfDay, startOfHour } from "date-fns";
import { IInteractionsCount, TimePeriodGroupBy } from "@edgetier/watchtower-types";

/**
 * Transform the interaction count data to be suitable for graphing.
 * @param queryData              Result of requesting interaction counts.
 * @param highlightStartDateTime Start of the date range to highlight.
 * @param highlightEndDateTime   End of the date range to highlight.
 * @param groupBy                Period to group results (hours, days etc.).
 * @returns                      Array of data with additional properties.
 */
export const formatChartData = (
    queryData: IInteractionsCount<Date>[] | undefined,
    highlightRange: boolean,
    highlightStartDateTime: Date | undefined,
    highlightEndDateTime: Date | null | undefined,
    groupBy: TimePeriodGroupBy
) => {
    const getStartOf = groupBy === TimePeriodGroupBy.Hours ? startOfHour : startOfDay;
    const getEndOf = groupBy === TimePeriodGroupBy.Hours ? endOfHour : endOfDay;
    return (queryData ?? []).map((row) => ({
        ...row,
        dateTime: row.group.getTime(),
        isHighlighted:
            highlightStartDateTime instanceof Date && highlightRange
                ? row.group >= getStartOf(highlightStartDateTime) &&
                  row.group <= getEndOf(highlightEndDateTime ?? new Date())
                : false,
    }));
};

/**
 * Tick interval for the first X axis
 * @param timePeriodGroup
 */
export const upperXAxisTickInterval = (timePeriodGroup: TimePeriodGroupBy) => {
    if (timePeriodGroup === TimePeriodGroupBy.Minutes) {
        return 4;
    }
    if (timePeriodGroup === TimePeriodGroupBy.Hours) {
        return 5;
    }
    return "preserveStart";
};

/**
 * Add proportion data. Divide each count from the request with (all) parameters by the count with same group from request
 * without any parameters. Using the same date range in requests, the groups are matched.
 * @param filteredQueryData Result of requesting interaction counts with parameters
 * @param genericQueryData  Result of requesting interaction counts only with time period group
 * @returns                  Array of data with calculated proportion
 */
export const applyProportionToQueryData = (
    filteredQueryData: IInteractionsCount<Date>[] | undefined,
    genericQueryData: IInteractionsCount<Date>[] | undefined
): IInteractionsCount<Date>[] | undefined => {
    if (typeof genericQueryData === "undefined") return filteredQueryData;

    return (filteredQueryData ?? []).map((row, index) => ({
        ...row,
        proportion:
            row.count === 0 || genericQueryData[index].count === 0 ? 0 : row.count / genericQueryData[index].count,
    }));
};
